// frontend/src/components/ResumeRecordingBanner.js

import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Box,
  Typography,
  Collapse,
  IconButton
} from '@mui/material';
import {
  PlayArrow,
  Close,
  Warning
} from '@mui/icons-material';
import { useRecording } from '../contexts/SimpleRecordingContext';

const ResumeRecordingBanner = () => {
  const [pendingSessions, setPendingSessions] = useState([]);
  const [showBanner, setShowBanner] = useState(false);
  const { resumeSession, isRecording } = useRecording();
  
  // IndexedDB constants
  const DB_NAME = 'VoiceRecording';
  const STORE_NAME = 'chunks';
  
  useEffect(() => {
    const checkForPendingSessions = async () => {
      if (isRecording) return; // Don't show banner if already recording
      
      try {
        const request = indexedDB.open(DB_NAME);
        
        request.onsuccess = (event) => {
          const db = event.target.result;
          
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            return;
          }
          
          const transaction = db.transaction([STORE_NAME], 'readonly');
          const store = transaction.objectStore(STORE_NAME);
          const getAllRequest = store.getAll();
          
          getAllRequest.onsuccess = () => {
            const allChunks = getAllRequest.result;
            
            // Group chunks by sessionId and find incomplete sessions
            const sessionMap = {};
            allChunks.forEach(chunk => {
              if (!chunk.uploaded) {
                if (!sessionMap[chunk.sessionId]) {
                  sessionMap[chunk.sessionId] = {
                    sessionId: chunk.sessionId,
                    chunkCount: 0,
                    oldestTimestamp: chunk.timestamp
                  };
                }
                sessionMap[chunk.sessionId].chunkCount++;
                sessionMap[chunk.sessionId].oldestTimestamp = Math.min(
                  sessionMap[chunk.sessionId].oldestTimestamp,
                  chunk.timestamp
                );
              }
            });
            
            const pending = Object.values(sessionMap).filter(session => 
              session.chunkCount > 0 && 
              Date.now() - session.oldestTimestamp < 24 * 60 * 60 * 1000 // Less than 24 hours old
            );
            
            if (pending.length > 0) {
              setPendingSessions(pending);
              setShowBanner(true);
            }
          };
        };
        
        request.onerror = () => {
          console.error('Failed to check for pending sessions');
        };
      } catch (error) {
        console.error('Error checking IndexedDB:', error);
      }
    };
    
    // Check after a short delay to ensure other components are initialized
    const timer = setTimeout(checkForPendingSessions, 1000);
    
    return () => clearTimeout(timer);
  }, [isRecording]);
  
  const handleResumeRecording = async () => {
    try {
      const newestSession = pendingSessions.reduce((newest, session) => 
        session.oldestTimestamp > newest.oldestTimestamp ? session : newest
      );
      await resumeSession(newestSession.sessionId);
      setShowBanner(false);
    } catch (error) {
      console.error('Failed to resume recording:', error);
    }
  };
  
  const handleDismiss = async () => {
    // Clear the incomplete recordings from IndexedDB
    try {
      const request = indexedDB.open(DB_NAME);
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          return;
        }
        
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        // Delete all chunks for pending sessions
        pendingSessions.forEach(session => {
          const index = store.index('sessionId');
          const getAllRequest = index.getAll(session.sessionId);
          
          getAllRequest.onsuccess = () => {
            const chunks = getAllRequest.result;
            chunks.forEach(chunk => {
              if (!chunk.uploaded) {
                store.delete(chunk.id);
              }
            });
          };
        });
      };
    } catch (error) {
      console.error('Error clearing incomplete recordings:', error);
    }
    
    setShowBanner(false);
    setPendingSessions([]);
  };
  
  const formatTimeAgo = (timestamp) => {
    const minutes = Math.floor((Date.now() - timestamp) / 60000);
    if (minutes < 60) {
      return `${minutes} minutes ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hours ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days} days ago`;
  };
  
  if (!showBanner || pendingSessions.length === 0) {
    return null;
  }
  
  const newestSession = pendingSessions.reduce((newest, session) => 
    session.oldestTimestamp > newest.oldestTimestamp ? session : newest
  );
  
  return (
    <Collapse in={showBanner}>
      <Alert
        severity="warning"
        sx={{
          mb: 2,
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="warning"
              size="small"
              startIcon={<PlayArrow />}
              onClick={handleResumeRecording}
              variant="contained"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Resume Recording
            </Button>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'warning.main' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Incomplete Recording Found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You have {newestSession.chunkCount} audio chunks from {formatTimeAgo(newestSession.oldestTimestamp)} 
            that weren't uploaded. Resume recording to continue where you left off.
          </Typography>
          {pendingSessions.length > 1 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              {pendingSessions.length - 1} other incomplete session{pendingSessions.length > 2 ? 's' : ''} found
            </Typography>
          )}
        </Box>
      </Alert>
    </Collapse>
  );
};

export default ResumeRecordingBanner;